import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import TimelineCard from "./TimelineCard";
import timelineBaby from "/assets/timeline-baby.jpg";
import timelineSchool from "/assets/timeline-school.jpg";
import timelineTeen from "/assets/timeline-teen.jpg";
import timelineAdult from "/assets/timeline-adult.jpg";

const timelineData = [
  {
    ageRange: "0-3 ans",
    title: "Les Premières Fois",
    image: timelineBaby,
    milestones: ["Naissance", "Premier sourire", "Premiers pas", "Premier anniversaire"],
    features: ["🎁 Liste de naissance", "📸 Album photo", "💬 Journal"],
    testimonial: {
      text: "Grâce à Legatia, 32 proches ont contribué à l'arrivée d'Emma. Nous avons collecté €3,200 que nous avons investi pour elle.",
      author: "Sophie & Marc",
      role: "parents d'Emma",
    },
    badge: "€3,200 collectés",
    color: "sunrise",
    cagnotteMoments: [
      { event: "🎂 Naissance", proches: 32, montant: "€3,200", futur: "~€14,300 à 18 ans" },
      { event: "🎉 1er anniversaire", proches: 28, montant: "€840", futur: "~€3,540 à 18 ans" },
      { event: "👶 Premiers pas", proches: 15, montant: "€450", futur: "~€1,900 à 18 ans" }
    ],
    totalInvesti: "€4,490",
    totalFutur: "~€19,740",
    croissance: "+340%"
  },
  {
    ageRange: "3-12 ans",
    title: "L'École et les Passions",
    image: timelineSchool,
    milestones: ["Première rentrée", "Passion danse", "Voyage scolaire CM2"],
    features: ["🎯 Cagnottes événements", "🎨 Galerie créative", "📊 Suivi croissance"],
    testimonial: {
      text: "Pour son voyage scolaire en Angleterre, 45 personnes ont participé ! Au lieu de courir après chacun, un simple lien a suffi.",
      author: "Grand-mère Monique",
      role: "",
    },
    badge: "45 contributeurs",
    color: "babyBlue",
    cagnotteMoments: [
      { event: "📚 Rentrée CP (6 ans)", proches: 24, montant: "€920", futur: "~€2,450 à 18 ans" },
      { event: "🩰 Cours de danse (8 ans)", proches: 18, montant: "€1,200", futur: "~€2,830 à 18 ans" },
      { event: "✈️ Voyage Angleterre (12 ans)", proches: 45, montant: "€2,800", futur: "~€5,380 à 18 ans" },
      { event: "🎂 Anniversaires (cumulés)", proches: 35, montant: "€2,100", futur: "~€4,520 à 18 ans" }
    ],
    totalInvesti: "€7,020",
    totalFutur: "~€15,180",
    croissance: "+116%"
  },
  {
    ageRange: "12-18 ans",
    title: "Les Projets et l'Autonomie",
    image: timelineTeen,
    milestones: ["Code de la route", "Choix des études", "Premier job d'été"],
    features: ["💰 Épargne progressive", "📈 Projection investissement", "🎓 Objectifs études"],
    testimonial: {
      text: "Depuis ses 6 ans, nous avons épargné 50€/mois. Avec les contributions des parrains, €12,000 investis sont devenus €28,500 pour ses études.",
      author: "Marc",
      role: "papa d'Emma",
    },
    badge: "€28,500 disponibles",
    color: "growth",
    cagnotteMoments: [
      { event: "🚗 Permis de conduire (16 ans)", proches: 28, montant: "€2,400", futur: "~€2,850 à 18 ans" },
      { event: "💻 Ordinateur études (15 ans)", proches: 22, montant: "€1,800", futur: "~€2,330 à 18 ans" },
      { event: "🎓 Orientation (17 ans)", proches: 12, montant: "€900", futur: "~€980 à 18 ans" }
    ],
    totalInvesti: "€5,100",
    totalFutur: "~€6,160",
    croissance: "+21%"
  },
  {
    ageRange: "18+ ans",
    title: "L'Envol et la Transmission",
    image: timelineAdult,
    milestones: ["Majorité", "Premier appartement", "Transmission"],
    features: ["💌 Messages scellés déverrouillés", "🔓 Coffre-fort émotionnel", "🌍 Héritage familial"],
    testimonial: {
      text: "À ses 18 ans, Emma a découvert 47 messages enregistrés depuis sa naissance, dont ceux de sa grand-mère disparue. Elle a pleuré pendant une heure.",
      author: "Sophie",
      role: "maman d'Emma",
    },
    badge: "47 messages scellés",
    color: "future",
    cagnotteMoments: [
      { event: "🎓 Capital total débloqué", total: "~€41,000", source: "18 ans de moments" }
    ],
    totalInvesti: "€16,610",
    totalFutur: "~€41,000",
    croissance: "+147%"
  },
];

const InteractiveTimeline = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="section-padding bg-gradient-to-b from-sunrise-light to-white relative overflow-hidden">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="section-title">Ses Moments, Son Avenir</h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            Découvrez comment les cagnottes-moment d'Emma construisent son capital et son album de vie de 0 à 18 ans
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {timelineData.map((_, index) => (
              <div key={index} className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-legatia-green flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
                {index < timelineData.length - 1 && (
                  <div className="w-12 h-0.5 bg-legatia-green" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Cards Container */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scroll("left")}
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-xl hover:shadow-2xl text-foreground p-3 rounded-full transition-all -translate-x-6"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-6 snap-x snap-mandatory scroll-smooth pb-4 hide-scrollbar"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {timelineData.map((data, index) => (
              <div key={index} className="snap-center flex-shrink-0">
                <TimelineCard {...data} index={index} />
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => scroll("right")}
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-xl hover:shadow-2xl text-foreground p-3 rounded-full transition-all translate-x-6"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Mobile Scroll Hint */}
        <div className="text-center mt-8 md:hidden">
          <div className="inline-block bg-legatia-green/20 text-legatia-green rounded-full px-6 py-3 shadow-lg">
            <p className="text-sm font-semibold">← Faites glisser pour voir la suite →</p>
          </div>
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default InteractiveTimeline;
